import { Component, Input, OnChanges, SimpleChanges, effect } from '@angular/core';
import { Filter, IdentifiedItemConfig } from '../../models/filter-models';
import { FormsModule } from '@angular/forms';
import { ItemDataService } from '../../services/item-data.service';
import { AffixType, BaseType, CustomModItemCategory, Mod, ModItemCategory, ModSlot, ModSlotData, ModTag, ModTierItemVm, ModTierType, TieredMod } from '../../models/item-data-models';
import { FilterService } from '../../services/filter.service';
import { BaseComponent } from '../../common/base.component';
import { PrimaryModLabelComponent } from '../../common/mod-labels/primary-mod-label/primary-mod-label.component';
import { ArmourType, AttackWeaponsCategory, CasterWeaponsCategory, DefenceType, GemWeaponsCategory, IdentifiedItemType, ITEM_CATEGORIES_POE1_ONLY, ITEM_CATEGORIES_POE2_ONLY, LifeArmourCategory, ModItemCategoryType, QuiversCategory, WeaponType, JewelleryCategory, EnergyShieldArmourCategory, CustomModItemType, JewelleryType, ModDomain, JewelType, JewelsCategory, TimeLostJewelsCategory } from '../../models/enums';
import { TooltipWrapperComponent } from '../../common/tooltip-wrapper/tooltip-wrapper.component';
import { ModTierService } from '../../services/tiering/mod-tier.service';
import { confirmRemove } from '../../common/confirm-remove';
import { GlobalShortcutFocusDirective } from '../../common/global-shortcut-focus.directive';
import { SelectComponent, SelectOptionComponent } from "../../common/select/select.component";
import { AutocompleteComponent } from "../../common/autocomplete/autocomplete.component";
import { SelectGearTierComponent } from "../../common/select/select-gear-tier/select-gear-tier.component";
import { SelectRarityComponent } from "../../common/select/select-rarity/select-rarity.component";
import { MultiModLabelComponent } from '../../common/mod-labels/multi-mod-label/multi-mod-label.component';
import { poe1, poe2 } from '../../../game';
// import { DynamicContentComponent } from '../../common/dynamic-content/dynamic-content.component';
import { ModTierExplainerComponent } from '../../common/mod-tier-explainer/mod-tier-explainer.component';
import { ModQualityFiltersComponent } from '../../common/mod-quality-filters/mod-quality-filters.component';
import { SelectComparatorComponent } from '../../common/select/select-comparator/select-comparator.component';
import { hasMovedFromOriginalTier } from '../tier-helpers';

interface ModItemCategoryVm extends ModItemCategory {
  mainSlots: ModSlot[];
  extraModSlots: ModSlot[];
  excludeModSlots: ModSlot[];
  modTags: ModTag[];
  excludeModTags: ModTag[];
  badModTags: ModTag[];
}

export interface ModTierVm {
  tier: ModTierType;
  tierStyle: string;
  icon: string;
  isDragOver?: boolean;
  dragCounter?: number;
  affixes: ModAffixVm[];
}

export interface ModAffixVm {
  affix: string;
  mods: ModTierItemVm[];
}

export interface ItemTypeTab {
  name: IdentifiedItemType;
  categories: ModItemCategoryType[] | string[];
}

export const ITEM_TYPES_POE1_ONLY = [
  IdentifiedItemType.AbyssJewels, IdentifiedItemType.GemWeapons
];

export const ITEM_TYPES_POE2_ONLY = [
  IdentifiedItemType.TimeLostJewels,
];

const JEWEL_SLOT_MAP: Record<string, string> = {
  ["Ruby"]: 'strjewel',
  ["Emerald"]: 'dexjewel',
  ["Sapphire"]: 'intjewel',
  ["Time-Lost Ruby"]: 'str_radius_jewel',
  ["Time-Lost Emerald"]: 'dex_radius_jewel',
  ["Time-Lost Sapphire"]: 'int_radius_jewel',
};

@Component({
  selector: 'app-mod-tiers',
  imports: [FormsModule, PrimaryModLabelComponent, TooltipWrapperComponent, GlobalShortcutFocusDirective, SelectComponent, SelectOptionComponent,
    AutocompleteComponent, AutocompleteComponent, SelectGearTierComponent, SelectRarityComponent, MultiModLabelComponent, ModTierExplainerComponent,
    ModQualityFiltersComponent, SelectComparatorComponent
  ],
  templateUrl: './mod-tiers.component.html',
  styleUrls: ['./mod-tiers.component.scss', '../tier-tab-common.scss', '../../common/mod-labels/mod-label.component.scss']
})
export class ModTiersComponent extends BaseComponent implements OnChanges {
  @Input({ required: true }) filter!: Filter;
  @Input() changedOnly = false;

  modData: Mod[] = [];
  modSlots: ModSlot[] = [];
  modTags: ModTag[] = [];
  modSlotData: ModSlotData[] = [];
  modItemCategories: ModItemCategory[] = [];
  weaponTypes: BaseType[] = [];
  armourTypes: BaseType[] = [];
  defenceTypes = [
    { name: 'Arm', image: '/img/shields/shield-red.png' },
    { name: 'Eva', image: '/img/shields/shield-green.png' },
    { name: 'ES', image: '/img/shields/shield-blue.png' },
    { name: 'Arm/Eva', image: '/img/shields/shield-red-green.png' },
    { name: 'Arm/ES', image: '/img/shields/shield-red-blue.png' },
    { name: 'Eva/ES', image: '/img/shields/shield-green-blue.png' }
  ];
  jewelleryTypes: BaseType[] = [];
  jewelTypes: BaseType[] = [];
  abyssJewelTypes: BaseType[] = [];
  timeLostJewelTypes: BaseType[] = [];

  selectedItemType: IdentifiedItemType = IdentifiedItemType.AttackWeapons;
  selectedItemCategory: ModItemCategoryType | string | undefined = AttackWeaponsCategory.MeleePhysical;
  selectedModItemCategory?: ModItemCategoryVm;
  selectedModDomain?: ModDomain = ModDomain.Equipment;
  selectedCustomModItemCategory?: CustomModItemCategory;
  modItemCategoriesForSelectedItemType: ModItemCategoryType[] | string[] = [];

  modTierList: ModTierVm[] = [];
  tierColumns: ModTierVm[][] = [];

  draggedItem: ModTierItemVm | null = null;

  modFilter: string = '';
  searchThroughAllMods: boolean = false;
  showAllMods: boolean = false;

  itemTypeTabs!: ItemTypeTab[];

  meleeWeaponOneHandSlots = ['one_hand_weapon', 'weapon', 'sword', 'axe', 'mace', 'rapier', 'claw', 'dagger'];
  meleeWeaponTwoHandSlots = ['two_hand_weapon', 'weapon', 'sword', 'axe', 'mace'];
  meleeWeaponSelectedSlotType = 'one_hand_weapon';

  oldName = '';
  renameError: string | null = null;

  AttackWeaponsCategory = AttackWeaponsCategory;
  IdentifiedItemType = IdentifiedItemType;
  CustomModItemType = CustomModItemType;

  constructor(
    private itemDataService: ItemDataService,
    private filterService: FilterService,
    private modTierService: ModTierService,
  ) {
    super();

    this.modSlots = this.itemDataService.getModSlots();
    this.modTags = this.itemDataService.getModTags();
    this.modItemCategories = this.itemDataService.getModItemCategories();
    this.weaponTypes = (this.itemDataService.getAllWeaponTypes(false) as WeaponType[]).map(w => {
      return { name: w, image: this.itemDataService.getItemData().find(i => i.itemClass === w)!.baseTypes[0].image! };
    });
    this.armourTypes = [
      { name: 'Body Armours', image: this.itemDataService.getItemData().find(i => i.itemClass === 'Body Armours')!.baseTypes[1].image! },
      { name: 'Helmets', image: this.itemDataService.getItemData().find(i => i.itemClass === 'Helmets')!.baseTypes[6].image! },
      { name: 'Gloves', image: this.itemDataService.getItemData().find(i => i.itemClass === 'Gloves')!.baseTypes[1].image! },
      { name: 'Boots', image: this.itemDataService.getItemData().find(i => i.itemClass === 'Boots')!.baseTypes[1].image! },
      { name: 'Shields', image: this.itemDataService.getItemData().find(i => i.itemClass === 'Shields')!.baseTypes[0].image! },
    ];
    this.jewelleryTypes = [
      { name: 'Amulets', image: this.itemDataService.getItemData().find(i => i.itemClass === 'Amulets')!.baseTypes[0].image! },
      { name: 'Rings', image: this.itemDataService.getItemData().find(i => i.itemClass === 'Rings')!.baseTypes[0].image! },
      { name: 'Belts', image: this.itemDataService.getItemData().find(i => i.itemClass === 'Belts')!.baseTypes[0].image! },
    ];
    this.jewelTypes = poe2 ? [
      { name: 'Ruby', image: this.itemDataService.getItemData().find(i => i.itemClass === 'Jewels')!.baseTypes[1].image! },
      { name: 'Emerald', image: this.itemDataService.getItemData().find(i => i.itemClass === 'Jewels')!.baseTypes[2].image! },
      { name: 'Sapphire', image: this.itemDataService.getItemData().find(i => i.itemClass === 'Jewels')!.baseTypes[3].image! },
    ] : [
      { name: 'Crimson', image: this.itemDataService.getItemData().find(i => i.itemClass === 'Jewels')!.baseTypes[0].image! },
      { name: 'Viridian', image: this.itemDataService.getItemData().find(i => i.itemClass === 'Jewels')!.baseTypes[1].image! },
      { name: 'Cobalt', image: this.itemDataService.getItemData().find(i => i.itemClass === 'Jewels')!.baseTypes[2].image! },
    ];
    this.abyssJewelTypes = poe2 ? [] : [
      { name: 'Murderous Eye', image: this.itemDataService.getItemData().find(i => i.itemClass === 'Abyss Jewels')!.baseTypes[0].image! },
      { name: 'Searching Eye', image: this.itemDataService.getItemData().find(i => i.itemClass === 'Abyss Jewels')!.baseTypes[1].image! },
      { name: 'Hypnotic Eye', image: this.itemDataService.getItemData().find(i => i.itemClass === 'Abyss Jewels')!.baseTypes[2].image! },
      { name: 'Ghastly Eye', image: this.itemDataService.getItemData().find(i => i.itemClass === 'Abyss Jewels')!.baseTypes[3].image! },
    ];
    this.timeLostJewelTypes = poe2 ? [
      { name: 'Time-Lost Ruby', image: this.itemDataService.getItemData().find(i => i.itemClass === 'Jewels')!.baseTypes[5].image! },
      { name: 'Time-Lost Emerald', image: this.itemDataService.getItemData().find(i => i.itemClass === 'Jewels')!.baseTypes[6].image! },
      { name: 'Time-Lost Sapphire', image: this.itemDataService.getItemData().find(i => i.itemClass === 'Jewels')!.baseTypes[7].image! },
    ] : [];

    if (poe1) {
      this.meleeWeaponOneHandSlots = ['one_hand_weapon', 'weapon', 'sword', 'axe', 'mace', 'rapier', 'claw', 'dagger'];
      this.meleeWeaponTwoHandSlots = ['two_hand_weapon', 'weapon', 'sword', 'axe', 'mace'];
    } else {
      this.meleeWeaponOneHandSlots = ['one_hand_weapon', 'weapon', 'mace', 'spear'];
      this.meleeWeaponTwoHandSlots = ['two_hand_weapon', 'weapon', 'mace', 'warstaff', 'talisman'];
    }

    effect(() => {
      const modDataSignal = this.itemDataService.getModData();
      if (modDataSignal().length > 0) {
        this.modData = modDataSignal();
        this.buildModTierList();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filter']) {
      this.buildItemTypeTabs();
      this.changeItemType(this.selectedItemType);
      this.buildModTierList();
    } else if (changes['changedOnly']) {
      this.buildModTierList();
    }
  }

  private buildItemTypeTabs() {
    this.itemTypeTabs = Object.values(IdentifiedItemType).filter(type => poe1 || !ITEM_TYPES_POE1_ONLY.includes(type)).filter(type => !poe1 || !ITEM_TYPES_POE2_ONLY.includes(type)).map(type => {
      if (type === IdentifiedItemType.Custom) {
        return {
          name: type,
          categories: this.filter.customModItemCategories.map(c => c.name)
        };
      }

      const categories = this.modItemCategories.filter(c => c.type === type).filter(c => poe1 || !ITEM_CATEGORIES_POE1_ONLY.includes(c.category)).filter(c => !poe1 || !ITEM_CATEGORIES_POE2_ONLY.includes(c.category)).map(c => c.category);
      return {
        name: type,
        categories
      };
    });
  }

  convertToModItemCategoryVm(category?: ModItemCategory): ModItemCategoryVm | undefined {
    if (!category) return undefined;
    const slots = category.category === AttackWeaponsCategory.MeleePhysical || category.category === AttackWeaponsCategory.MeleeElemental
      ? this.meleeWeaponSelectedSlotType === 'one_hand_weapon'
        ? this.meleeWeaponOneHandSlots
        : this.meleeWeaponTwoHandSlots
      : category.slots;
    const modSlots = slots.map(s => this.modSlots.find(m => m.techName === s) || this.modSlots.find(m => m.techName === 'default')!);
    const mainSlots = modSlots.slice(0, Math.min(2, modSlots.length));
    const extraSlots = modSlots.slice(Math.min(2, modSlots.length));

    return {
      ...category,
      includeGoodModTypes: category.includeGoodModTypes.map(t => t.replace(/([A-Z])/g, ' $1').trim()),
      includeGreatModTypes: category.includeGreatModTypes.map(t => t.replace(/([A-Z])/g, ' $1').trim()),
      excludeModTypes: category.excludeModTypes.map(t => t.replace(/([A-Z])/g, ' $1').trim()),
      mainSlots: mainSlots,
      extraModSlots: extraSlots,
      excludeModSlots: category.excludeSlots.map(s => this.modSlots.find(m => m.techName === s) || this.modSlots.find(m => m.techName === 'default')!),
      modTags: category.tags.map(t => this.modTags.find(mt => mt.techName === t) || { id: t, name: t, abbr: t, color: 'tmt11', techName: t }),
      excludeModTags: category.excludeTags.map(t => this.modTags.find(mt => mt.techName === t) || { id: t, name: t, abbr: t, color: 'tmt11', techName: t }),
      badModTags: category.badTags.map(t => this.modTags.find(mt => mt.techName === t) || { id: t, name: t, abbr: t, color: 'tmt11', techName: t }),
    };
  }

  switchMeleeWeaponSlotType(): void {
    this.meleeWeaponSelectedSlotType = this.meleeWeaponSelectedSlotType === 'one_hand_weapon' ? 'two_hand_weapon' : 'one_hand_weapon';
    this.selectedModItemCategory = this.convertToModItemCategoryVm(this.modItemCategories.find(c => c.category === this.selectedItemCategory));
    this.buildModTierList();
  }

  buildModTierList() {
    const modTierData = this.modTierService.getModTierData();
    const tiers: ModTierType[] = ['Perfect', 'Great', 'Good', 'Neutral', 'Bad'];

    const modTierList = tiers.map(tier => {
      const vms = this.buildModTierItemVm(modTierData, tier);
      return {
        tier,
        tierStyle: this.getTierStyle(tier),
        icon: this.getTierIcon(tier),
        affixes: [
          { affix: 'Prefixes', mods: vms.filter(m => m.affixType === 'Prefix') },
          { affix: 'Suffixes', mods: vms.filter(m => m.affixType === 'Suffix') }
        ]
      };
    });

    if ((this.modFilter?.length >= 2 || this.showAllMods) && this.modData?.length) {
      const allExistingModNames = modTierList
        .filter(t => t.tier !== 'Neutral')
        .flatMap(t => t.affixes.flatMap(a => a.mods?.flatMap(m => m.name)));

      const matchingUnAssignedMods = this.modData
        .filter(m => !allExistingModNames.includes(m.name)
          && (this.selectedModDomain ? m.domain === this.selectedModDomain : true)
          && ((this.showAllMods && !this.modFilter?.length) || this.modContainsSearchText(m)));

      const neutralTieredPrefixes = this.buildNeutralTieredMods(matchingUnAssignedMods, 'Prefix');
      const neutralTieredSuffixes = this.buildNeutralTieredMods(matchingUnAssignedMods, 'Suffix');

      const neutralTier = modTierList.find(t => t.tier === 'Neutral')!;
      const neutralPrefixes = neutralTier.affixes.find(a => a.affix === 'Prefixes');
      const neutralSuffixes = neutralTier.affixes.find(a => a.affix === 'Suffixes');

      neutralPrefixes!.mods.length = 0;
      neutralSuffixes!.mods.length = 0;
      neutralPrefixes!.mods.push(...neutralTieredPrefixes);
      neutralSuffixes!.mods.push(...neutralTieredSuffixes);
    }

    if (this.changedOnly) {
      modTierList.forEach(tierVm => {
        tierVm.affixes.forEach(affix => {
          affix.mods = affix.mods.filter(m => hasMovedFromOriginalTier(m.originalTier, m.tier));
        });
      });
    }

    // Sort the mods of each tier first by putting the ones which have a primary mod first, then the other ones.
    // - for the ones with a primary mod: alphabetically by their primary mod's type, then by their lvl decreasing
    // - for the other ones, alphabetically by their name
    modTierList.forEach(tier => {
      tier.affixes.forEach(affix => {
        affix.mods.sort((a, b) => {
          // First, sort by whether they have a primary mod (primary mods first)
          const aHasPrimary = !!a.primaryMod;
          const bHasPrimary = !!b.primaryMod;

          if (aHasPrimary && !bHasPrimary) return -1;
          if (!aHasPrimary && bHasPrimary) return 1;

          // If both have primary mods or both don't have primary mods
          if (aHasPrimary && bHasPrimary) {
            // Both have primary mods: sort by primary mod's type alphabetically, then by level
            const typeComparison = (a.primaryMod!.type || '').localeCompare(b.primaryMod!.type || '');
            if (typeComparison !== 0) return typeComparison;

            // If types are the same, sort by level decreasing
            return (b.primaryMod!.lvl || 0) - (a.primaryMod!.lvl || 0);
          } else {
            // Both don't have primary mods: sort alphabetically by name
            return (a.name || '').localeCompare(b.name || '');
          }
        });
      });
    });

    // Group tiers into columns: Perfect and Great in same column, others separate
    this.tierColumns = [
      [], // Column 0: Perfect and Great
      [], // Column 1: Good
      [], // Column 2: Neutral
      [], // Column 3: Bad
    ];

    for (let tier of modTierList) {
      if (tier.tier === 'Perfect' || tier.tier === 'Great') {
        this.tierColumns[0].push(tier);
      } else if (tier.tier === 'Good') {
        this.tierColumns[1].push(tier);
      } else if (tier.tier === 'Neutral') {
        this.tierColumns[2].push(tier);
      } else if (tier.tier === 'Bad') {
        this.tierColumns[3].push(tier);
      }
    }

    // Sort Perfect before Great in the first column
    this.tierColumns[0].sort((a, b) => {
      if (a.tier === 'Perfect' && b.tier === 'Great') return -1;
      if (a.tier === 'Great' && b.tier === 'Perfect') return 1;
      return 0;
    });

    // Keep modTierList for backward compatibility if needed
    this.modTierList.length = 0;
    this.modTierList.push(...modTierList);
  }

  buildNeutralTieredMods(matchingUnAssignedMods: Mod[], affixType: AffixType): ModTierItemVm[] {
    const matchingUnassignedAffixNames = [...new Set(matchingUnAssignedMods.filter(m => m.affixType === affixType).map(m => m.name))];

    let neutralTieredMods = matchingUnassignedAffixNames
      .map(mt => ({
        cat: this.selectedItemCategory,
        name: mt,
        affixType: affixType,
        tier: 'Neutral',
        mods: this.modData.filter(m => m.name === mt)
      } as ModTierItemVm satisfies ModTierItemVm));

    neutralTieredMods.forEach(m => this.setRelevantAndPrimaryMods(m));

    if (this.selectedItemType !== IdentifiedItemType.Custom) {
      if (!this.searchThroughAllMods) {
        neutralTieredMods = neutralTieredMods.filter(m => m.applicableMods?.length && m.applicableMods.length > 0);
      }

      neutralTieredMods.forEach(mt => {
        mt.possiblyIncorrectSlot = !mt.applicableMods?.some(m => this.modTierService.modCanSpawnOnCategory(m, this.selectedModItemCategory!));

        // For jewels, we don't need to filter on tags
        if (this.selectedItemType === IdentifiedItemType.Jewels || this.selectedItemType === IdentifiedItemType.TimeLostJewels || this.selectedItemType === IdentifiedItemType.AbyssJewels) {
          mt.noApplicableTag = false;
        } else {
          // If a mod has no tags at all, always consider it matching (don't set noApplicableTag to true)
          mt.noApplicableTag = !mt.applicableMods?.some(m => {
            // If mod has no tags, consider it matching
            if (m.tags.length === 0) return true;
            // Otherwise, check if any tag matches
            return m.tags.some(tag => this.selectedModItemCategory?.modTags.map(t => t.id).includes(tag.id));
          });
        }
      });
    } else if (poe2 && (this.selectedCustomModItemCategory?.itemType === CustomModItemType.Jewel || this.selectedCustomModItemCategory?.itemType === CustomModItemType.TimeLostJewel)) {
      if (this.selectedCustomModItemCategory?.jewelBaseTypes.length === 1 && this.selectedCustomModItemCategory?.jewelBaseTypes[0] !== 'All') {
        const selectedCustomJewelBaseType = this.selectedCustomModItemCategory?.jewelBaseTypes[0];
        const slot = JEWEL_SLOT_MAP[selectedCustomJewelBaseType];
        neutralTieredMods.forEach(mt => {
          mt.possiblyIncorrectSlot = !mt.applicableMods?.some(m => this.modTierService.modCanSpawnOnSlots(m, [slot]));
        });
      }
    }

    if (!this.searchThroughAllMods) {
      neutralTieredMods = neutralTieredMods.filter(m => !m.possiblyIncorrectSlot && !m.noApplicableTag);
    }

    return neutralTieredMods;
  }

  buildModTierItemVm(modTierData: TieredMod[], tier: ModTierType): ModTierItemVm[] {
    let selectedMods = modTierData.filter(t => t.cat === this.selectedItemCategory && t.tier === tier);

    if (this.modFilter?.length >= 2) {
      selectedMods = selectedMods.filter(m => this.modsContainSearchText(m.mods));
    }

    const modTierItemVms = selectedMods.map(mod => mod as ModTierItemVm);

    modTierItemVms.forEach(m => {
      this.setRelevantAndPrimaryMods(m);
    });

    return modTierItemVms;
  }

  private setRelevantAndPrimaryMods(mod: ModTierItemVm) {
    if (!this.selectedModItemCategory) {
      mod.applicableMods = mod.mods?.filter(m => this.selectedModDomain ? m.domain === this.selectedModDomain : true) || [];
      mod.relevantMods = mod.applicableMods;

      if (this.modFilter?.length >= 2) {
        mod.relevantMods = mod.relevantMods?.filter(m => this.modContainsSearchText(m));
      }

      mod.nonRelevantMods = mod.applicableMods?.filter(m => !mod.relevantMods?.some(rm => rm.id === m.id));
      mod.nonApplicableMods = [];

      if (mod.relevantMods?.length === 1) {
        mod.primaryMod = mod.relevantMods[0];
      } else if (mod.mods?.length === 1) {
        mod.primaryMod = mod.mods[0];
      } else {
        mod.primaryMod = undefined;
      }

      return;
    }

    mod.applicableMods = mod.mods?.filter(m => {
      if (m.name !== mod.name) return false;
      if (this.selectedModDomain && m.domain !== this.selectedModDomain) return false;

      // Always include specific mod types
      if (this.selectedModItemCategory!.includeGreatModTypes.includes(m.type) || this.selectedModItemCategory!.includeGoodModTypes.includes(m.type)) return true;

      return this.modTierService.modCanSpawnOnCategory(m, this.selectedModItemCategory!);
    });

    let modsToConsider = mod.applicableMods;

    if (this.searchThroughAllMods) {
      modsToConsider = mod.mods;
    }

    mod.relevantMods = modsToConsider?.filter(m => {
      if (this.modFilter?.length >= 2) {
        return this.modContainsSearchText(m);
      }

      // Exclude mods which were explicitly listed to exclude
      if (this.selectedModItemCategory!.excludeModTypes.includes(m.type)) return false;

      // If the mod has any tags, exclude mods which don't have any of the selected mod item category's tags
      // If the mod has no tags at all, always consider it matching
      if (m.tags.length > 0 && !m.tags.some(tag => this.selectedModItemCategory!.tags.includes(tag.techName))) return false;

      // Exclude mods which have any of the selected mod item category's exclude tags
      if (this.selectedModItemCategory!.excludeTags.some(tag => m.tags.some(t => t.techName === tag))) return false;

      return true;
    }) || [];

    // Special case: melee physical and elemental weapons: if 'One hand' is selected, remove all duplicate mods with '2 hand' slot
    if (this.selectedItemCategory === AttackWeaponsCategory.MeleePhysical || this.selectedItemCategory === AttackWeaponsCategory.MeleeElemental) {
      if (this.meleeWeaponSelectedSlotType === 'one_hand_weapon') {
        // Ensure mods with 'two_hand_weapon' slot are excluded if a mod with the same name exists for 'one_hand_weapon'
        mod.relevantMods = mod.relevantMods.filter((m: Mod, _, mods) => {
          const hasOneHandVariant = mods.some(m2 => m2.id !== m.id && m2.name === m.name && (m2.slots.some(s => s.techName === 'one_hand_weapon') || m2.exSlots.some(s => s.techName === 'two_hand_weapon')));
          return !hasOneHandVariant || (!m.slots.some(s => s.techName === 'two_hand_weapon') && !m.exSlots.some(s => s.techName === 'one_hand_weapon'));
        });
      } else if (this.meleeWeaponSelectedSlotType === 'two_hand_weapon') {
        // Ensure mods with 'one_hand_weapon' slot are excluded if a mod with the same name exists for 'two_hand_weapon'
        mod.relevantMods = mod.relevantMods.filter((m: Mod, _, mods) => {
          const hasTwoHandVariant = mods.some(m2 => m2.id !== m.id && m2.name === m.name && (m2.slots.some(s => s.techName === 'two_hand_weapon') || m2.exSlots.some(s => s.techName === 'one_hand_weapon')));
          return !hasTwoHandVariant || (!m.slots.some(s => s.techName === 'one_hand_weapon') && !m.exSlots.some(s => s.techName === 'two_hand_weapon'));
        });
      }
    }

    mod.nonApplicableMods = mod.applicableMods?.filter(m => !mod.applicableMods?.some(am => am.id === m.id));
    mod.nonRelevantMods = mod.applicableMods?.filter(m => !mod.relevantMods?.some(rm => rm.id === m.id));

    if (mod.mods?.length === 1) {
      mod.primaryMod = mod.mods[0];
    } else if (mod.relevantMods?.length === 1) {
      mod.primaryMod = mod.relevantMods[0];
    } else if (mod.applicableMods?.length === 1) {
      mod.primaryMod = mod.applicableMods[0];
    } else {
      mod.primaryMod = undefined;
    }
  }

  private modsContainSearchText(mods?: Mod[]) {
    return mods?.some(m => this.modContainsSearchText(m));
  }

  private modContainsSearchText(mod: Mod) {
    return mod.name?.toLowerCase().includes(this.modFilter.toLowerCase()) ||
      mod.text?.toLowerCase().includes(this.modFilter.toLowerCase()) ||
      mod.type?.toLowerCase().includes(this.modFilter.toLowerCase()) ||
      mod.tags?.some(t => (t.name + ' ' + t.abbr + ' ' + t.techName).toLowerCase().includes(this.modFilter.toLowerCase()));
  }

  getTierStyle(tier: ModTierType): string {
    switch (tier) {
      case 'Perfect': return 'tier-dark-green';
      case 'Great': return 'tier-green';
      case 'Good': return 'tier-yellow';
      case 'Neutral': return 'tier-shown';
      case 'Bad': return 'tier-red';
      default: return 'tier-shown';
    }
  }

  getTierIcon(tier: ModTierType): string {
    switch (tier) {
      case 'Perfect': return 'icon-perfect-mod';
      case 'Great': return 'icon-great-mod';
      case 'Good': return 'icon-good-mod';
      case 'Neutral': return '';
      case 'Bad': return 'icon-bad-mod';
    }
  }

  changeItemType(itemType: IdentifiedItemType): void {
    this.selectedItemType = itemType;
    const itemTypeTab = this.itemTypeTabs.find(tab => tab.name === itemType);
    const itemCategory = itemTypeTab?.categories[0];

    if (this.selectedItemType === IdentifiedItemType.Custom) {
      this.modItemCategoriesForSelectedItemType = this.filter.customModItemCategories.map(c => c.name);
    } else {
      const currentItemType = this.itemTypeTabs.find(tab => tab.name === this.selectedItemType);
      this.modItemCategoriesForSelectedItemType = currentItemType?.categories || [];
    }

    if (itemType === IdentifiedItemType.Jewels || itemType === IdentifiedItemType.TimeLostJewels || itemType === IdentifiedItemType.AbyssJewels) {
      this.showAllMods = true;
    } else {
      this.showAllMods = false;
    }

    this.changeItemCategory(itemCategory);
    this.buildModTierList();
  }

  changeItemCategory(itemCategory?: ModItemCategoryType | string): void {
    if (!itemCategory) {
      this.selectedModItemCategory = undefined;
      this.selectedCustomModItemCategory = undefined;
    }

    this.selectedItemCategory = itemCategory;
    if (this.selectedItemType === IdentifiedItemType.Custom) {
      this.selectedCustomModItemCategory = this.filter.customModItemCategories.find(c => c.name === itemCategory);
      this.selectedModItemCategory = undefined;
      this.customItemTypeChanged();
    } else {
      this.selectedModItemCategory = this.convertToModItemCategoryVm(this.modItemCategories.find(c => c.category === itemCategory));
      this.selectedCustomModItemCategory = undefined;
      this.selectedModDomain = this.selectedModItemCategory?.type === IdentifiedItemType.Jewels
        ? ModDomain.Jewel
        : this.selectedModItemCategory?.type === IdentifiedItemType.AbyssJewels
          ? ModDomain.AbyssJewel
          : this.selectedModItemCategory?.type === IdentifiedItemType.TimeLostJewels
            ? ModDomain.TimeLostJewel
            : ModDomain.Equipment;
    }
    this.modFilter = '';
    this.buildModTierList();
  }

  customItemTypeChanged() {
    const domain = this.selectedCustomModItemCategory?.itemType === CustomModItemType.Jewel
      ? ModDomain.Jewel
      : this.selectedCustomModItemCategory?.itemType === CustomModItemType.AbyssJewel
        ? ModDomain.AbyssJewel
        : this.selectedCustomModItemCategory?.itemType === CustomModItemType.TimeLostJewel
          ? ModDomain.TimeLostJewel
          : this.selectedCustomModItemCategory?.itemType === CustomModItemType.Any
            ? undefined
            : ModDomain.Equipment;

    this.selectedModDomain = domain;
  }

  addNewCustomModItemCategory() {
    let uniqueName = 'Custom Category';
    let i = 1;
    while (this.filter.customModItemCategories.some(c => c.name === uniqueName)) {
      uniqueName = `Custom Category ${i}`;
      i++;
    }

    this.filter.customModItemCategories.push(new CustomModItemCategory({ name: uniqueName }));

    this.updateFilter();
    this.buildItemTypeTabs();
    this.changeItemType(IdentifiedItemType.Custom);
    this.changeItemCategory(uniqueName);
    this.customItemTypeChanged();
  }

  onDragStart(event: DragEvent, item: ModTierItemVm): void {
    this.draggedItem = item;
    event.dataTransfer?.setData('item', JSON.stringify(item));
  }

  onDragEnd(): void {
    this.draggedItem = null;
  }

  onDragEnter(event: DragEvent, tier: ModTierVm): void {
    event.preventDefault();
    tier.dragCounter = (tier.dragCounter || 0) + 1;
    tier.isDragOver = true;
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.dataTransfer!.dropEffect = 'move';
  }

  onDragLeave(event: DragEvent, tier: ModTierVm): void {
    tier.dragCounter = (tier.dragCounter || 0) - 1;
    if (tier.dragCounter === 0) {
      tier.isDragOver = false;
    }
  }

  onDrop(event: DragEvent, targetTier: ModTierVm): void {
    event.preventDefault();
    targetTier.isDragOver = false;
    targetTier.dragCounter = 0;
    const itemJson = event.dataTransfer!.getData('item');
    if (!itemJson) return;
    const item = JSON.parse(itemJson);
    this.filterService.updateModTier(item, targetTier.tier);
    this.buildModTierList();
    this.draggedItem = null;
  }

  resetToOriginalTier(item: ModTierItemVm): void {
    this.filterService.resetModTier(item);
    this.buildModTierList();
  }

  updateFilter(): void {
    this.filterService.updateFilter();
  }

  hideInfoMessage() {
    this.settings.dismissModsTierListMessage();
  }

  nameChangeKeyDown(event: KeyboardEvent){
    if (event.key === 'Enter') {
      event.preventDefault();
      this.changeName(event);
      return;
    }

    const allowedChars = /^[a-zA-Z0-9 .-]$/;
    const char = event.key;

    // Allow navigation keys (arrows, backspace, delete, etc.)
    if (event.ctrlKey || event.altKey || event.metaKey ||
      char.length > 1 || char === 'Backspace' || char === 'Delete' ||
      char === 'ArrowLeft' || char === 'ArrowRight' ||
      char === 'Tab' || char === 'Home' || char === 'End') {
      return;
    }

    // Prevent input of disallowed characters
    if (!allowedChars.test(char)) {
      event.preventDefault();
    }
  }

  changeName(event: Event) {
    this.renameError = null;
    const newName = (event.target as HTMLInputElement).value;
    if (!newName) {
      this.renameError = 'Name cannot be empty';
      return;
    }

    const existingNames = this.filter.customModItemCategories.map(c => c.name)
     .concat(Object.values(AttackWeaponsCategory))
     .concat(Object.values(CasterWeaponsCategory))
     .concat(Object.values(GemWeaponsCategory))
     .concat(Object.values(QuiversCategory))
     .concat(Object.values(LifeArmourCategory))
     .concat(Object.values(EnergyShieldArmourCategory))
     .concat(Object.values(JewelleryCategory));

    if (existingNames.filter(n => n === newName).length > 1) {
      (event.target as HTMLInputElement).value = this.oldName;
      this.renameError = `"${newName}" already exists. Please choose a different name.`;
      return;
    }

    this.filter.customModTiers.forEach(t => {
      if (t.cat === this.oldName) t.cat = newName;
    });

    this.selectedCustomModItemCategory!.name = newName;
    this.buildItemTypeTabs();
    this.updateFilter();
    this.modTierService.combineModTierListData(this.filter);
    this.changeItemType(IdentifiedItemType.Custom);
    this.changeItemCategory(newName);
  }

  nameFieldFocus(event: FocusEvent) {
    this.oldName = (event.target as HTMLInputElement).value;
    (event.target as HTMLInputElement).select();
  }

  deleteCustomModItemCategory() {
    if (confirmRemove(`Are you sure you want to delete the Item Category '${this.selectedCustomModItemCategory!.name}'? This cannot be undone.`)) {
      this.filter.customModItemCategories = this.filter.customModItemCategories.filter(c => c.name !== this.selectedCustomModItemCategory!.name);
      this.filter.customModTiers = this.filter.customModTiers.filter(t => t.cat !== this.selectedCustomModItemCategory!.name);
      this.updateFilter();
      this.changeItemType(IdentifiedItemType.Custom);
      this.changeItemCategory(this.filter.customModItemCategories[0]?.name);
    }
  }

  weaponClassesChanged(weapon: CustomModItemCategory, weaponTypes: string[]) {
    weapon.weaponClasses = weaponTypes.map(w => w as WeaponType);
    this.updateFilter();
  }

  armourTypesChanged(armour: CustomModItemCategory, armourTypes: string[]) {
    armour.armourTypes = armourTypes.map(a => a as ArmourType);
    this.updateFilter();
  }

  defenceTypesChanged(armour: CustomModItemCategory, defenceTypes: string[]) {
    armour.defenceTypes = defenceTypes.map(d => d as DefenceType);
    this.updateFilter();
  }

  jewelleryClassesChanged(jewellery: CustomModItemCategory, jewelleryTypes: string[]) {
    jewellery.jewelleryClasses = jewelleryTypes.map(j => j as JewelleryType);
    this.updateFilter();
  }

  jewelTypesChanged(jewel: CustomModItemCategory, jewelTypes: string[]) {
    jewel.jewelBaseTypes = jewelTypes.map(j => j as JewelType);
    this.updateFilter();
  }

  abyssJewelTypesChanged(abyssJewel: CustomModItemCategory, abyssJewelTypes: string[]) {
    abyssJewel.jewelBaseTypes = abyssJewelTypes.map(j => j as JewelType);
    this.updateFilter();
  }

  timeLostJewelTypesChanged(timeLostJewel: CustomModItemCategory, timeLostJewelTypes: string[]) {
    timeLostJewel.jewelBaseTypes = timeLostJewelTypes.map(j => j as JewelType);
    this.updateFilter();
  }

  toggleCustomConfiguration() {
    if (this.selectedCustomModItemCategory!.customConfiguration) {
      this.selectedCustomModItemCategory!.customConfiguration!.enabled = !this.selectedCustomModItemCategory!.customConfiguration!.enabled;
    } else {
      this.selectedCustomModItemCategory!.customConfiguration = new IdentifiedItemConfig();
    }
    this.buildModTierList();
    this.updateFilter();
  }
}
